import { first, isEmpty, last } from 'lodash';
import { useEffect, useRef } from 'react';
import { getController } from '@shared/util/timeout';

const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"]:not([disabled]))',
].join(',');

const ARROW_KEYS_ENABLED = false;

export function useFocusTrap(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!enabled || !container) {
      return;
    }
    container.dataset.focusTrap = 'true';
    requestAnimationFrame(() => container.focus());

    const onKeyDown = (e: KeyboardEvent) => {
      const containerHasChildTrap = container.querySelector('[data-focus-trap="true"]');
      if (containerHasChildTrap) {
        return;
      }
      const elements = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (isEmpty(elements)) {
        return;
      }

      switch (e.key) {
        case 'Tab':
          handleTabKey(e, elements);
          break;
        case 'ArrowUp':
          handleArrowUpKey(e, elements);
          break;
        case 'ArrowDown':
          handleArrowDownKey(e, elements);
          break;
        default:
          break;
      }
    };

    const { signal, abort } = getController();
    container.addEventListener('keydown', onKeyDown, { signal });

    return () => {
      container.dataset.focusTrap = 'false';
      abort();
    };
  }, [ref, enabled]);

  return ref;
}

function handleTabKey(e: KeyboardEvent, elements: HTMLElement[]) {
  const firstElement = first(elements);
  const lastElement = last(elements);
  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault();
    lastElement?.focus();
  } else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault();
    firstElement?.focus();
  }
}

function handleArrowUpKey(e: KeyboardEvent, elements: HTMLElement[]) {
  if (!ARROW_KEYS_ENABLED) {
    return;
  }
  if (isTextInput(document.activeElement as HTMLElement)) {
    return;
  }
  // focus the previous element
  const elementIndex = elements.indexOf(document.activeElement as HTMLElement);
  const previousElement = elements[elementIndex - 1] || last(elements);
  if (previousElement) {
    e.preventDefault();
    previousElement.focus();
  }
}

function handleArrowDownKey(e: KeyboardEvent, elements: HTMLElement[]) {
  if (!ARROW_KEYS_ENABLED) {
    return;
  }
  if (isTextInput(document.activeElement as HTMLElement)) {
    return;
  }
  const elementIndex = elements.indexOf(document.activeElement as HTMLElement);
  const nextElement = elements[elementIndex + 1] || first(elements);
  if (nextElement) {
    e.preventDefault();
    nextElement.focus();
  }
}

function isTextInput(element: HTMLElement | null) {
  return element?.tagName === 'INPUT' || element?.tagName === 'TEXTAREA';
}
